
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, Subject, Subscription } from 'rxjs';
import { LangType } from '../model/types/GlobalTypes';
import { TranslateService } from '@ngx-translate/core';
import { ToastrService } from 'ngx-toastr';
import { Title } from '@angular/platform-browser';
import { ResponseMessage } from '../interfaces/req.res.interface';
import { HttpClient } from '@angular/common/http';
import { defaultLanguage } from '../model/const/common.const';
import { LocalStorageService } from './local-storage.service';
import { selectedStoreId } from '../model/const/local-storage.const';
import { EncryptDecryptService } from './encryption.service';
import { Router } from '@angular/router';

@Injectable({
  providedIn: 'root'
})
export class ConstantService {



  readonly translate = inject(TranslateService);
  readonly localStorageService = inject(LocalStorageService);
  readonly encryptDecryptService = inject(EncryptDecryptService);
  readonly http = inject(HttpClient);
  readonly toastr = inject(ToastrService);
  readonly title = inject(Title);
  readonly router = inject(Router); // Router for handling navigation within the application

  progressLoader = new BehaviorSubject(false);
  showToolBar = new BehaviorSubject(false);
  readonly _changePage = new BehaviorSubject<boolean>(false);  // Or default value
  public changePage = this._changePage.asObservable();
  langChangeSub = new Subject();
  readonly storeChangedSubject = new Subject<boolean>();
  public storeChanged$ = this.storeChangedSubject.asObservable();

  constructor() { }

  redirectToURL(url: string, newTab: string) {
    window.open(url, newTab);
  }


  changePageTrigger(value: boolean): void {
    this._changePage.next(value);
  }

  showSuccess(messageKey: string) {
    this.translate.get(messageKey).subscribe((message: string) => {
      this.toastr.success(message);
    });
  }

  showError(messageKey: string) {
    this.translate.get(messageKey).subscribe((message: string) => {
      this.toastr.error(message);
    });
  }
  setModuleTitle(name: string) {
    this.title.setTitle(name + ' | MuyBi');
  }

  getPermissionList(backOfficeAccess: number): Observable<ResponseMessage> {
    return this.http.get<ResponseMessage>('roles/permissions?is_backoffice_access=' + backOfficeAccess);
  }

  getDynamicPages(currentPage: number, totalPages: number): (number | string)[] {
    const pages: (number | string)[] = [];
    const seen = new Set<number>();

    if (totalPages <= 1) {
      return [1];
    }

    // Helper function to add page if not already added
    const addPage = (page: number) => {
      if (!seen.has(page) && page >= 1 && page <= totalPages) {
        seen.add(page);
        pages.push(page);
      }
    };

    // Always show first page
    addPage(1);

    // If total pages is small (7 or less), show all pages
    if (totalPages <= 7) {
      for (let i = 2; i <= totalPages; i++) {
        addPage(i);
      }
      return pages;
    }

    // Calculate center pages around current page
    let startPage = Math.max(2, currentPage - 1);
    let endPage = Math.min(totalPages - 1, currentPage + 1);

    // Adjust if near the beginning
    if (currentPage <= 3) {
      startPage = 2;
      endPage = 4;
    }

    // Adjust if near the end
    if (currentPage >= totalPages - 2) {
      startPage = totalPages - 3;
      endPage = totalPages - 1;
    }

    // Show ellipsis before center if there's a gap
    if (startPage > 2) {
      pages.push('...');
    }

    // Add center pages
    for (let i = startPage; i <= endPage; i++) {
      addPage(i);
    }

    // Show ellipsis after center if there's a gap
    if (endPage < totalPages - 1) {
      pages.push('...');
    }

    // Always show last page
    addPage(totalPages);

    return pages;
  }

  getStoreId() {
    const encryptedStore = this.localStorageService.getItem(selectedStoreId);
    const storeId = encryptedStore ? this.encryptDecryptService.getDecrypt(encryptedStore) : 0;
    return Number(storeId);
  }


  getLanguage(): LangType {
    const storedLanguage = localStorage.getItem('language') as LangType;
    return storedLanguage ?? defaultLanguage;
  }
  /**
 * Get previous 12 months dropdown options
 */
  getPrevious12Months(): { name: string; value: string }[] {
    const locale = this.getLanguage();
    const months = [];
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      const monthName = date.toLocaleString(locale, {
        month: 'long',
        year: 'numeric',
      });
      const year = date.getFullYear();
      const value = `${year}-${('0' + (date.getMonth() + 1)).slice(-2)}`; // Format YYYY-MM
      months.push({ name: monthName, value });
    }
    return months;
  }

  notifyStoreChanged() {
    this.storeChangedSubject.next(true);
  }
  subscribeToStoreChange(callbacks: { [route: string]: () => void }): Subscription {
    return this.storeChanged$.subscribe(() => {
      const currentRoute = this.router.url;
      Object.keys(callbacks).forEach(routeKey => {
        if (currentRoute.includes(routeKey)) {
          callbacks[routeKey]();
        }
      });
    });
  }

  downloadFile(fileName: string, fileUrl: string): void {
    if (!fileUrl) {
      console.error('Invalid file url');
      return;
    }

    fetch(fileUrl)
      .then(response => response.blob())
      .then(blob => {
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${fileName}.pdf`;
        a.click();
        window.URL.revokeObjectURL(url);
      })
      .catch(() => {
        this.redirectToURL(fileUrl, '_blank');
      });
  }

}
